import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ProfileTypePicker, profileLabel } from "@/components/ProfileTypePicker";
import { Loader2, UserCog } from "lucide-react";

export default function ProfileTypeModal({ open, onOpenChange, required = false }) {
  const { user, setProfileType } = useAuth();
  const [value, setValue] = useState(user?.profile_type || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(user?.profile_type || "");
  }, [open, user?.profile_type]);

  const save = async () => {
    setSaving(true);
    try {
      await setProfileType(value);
      toast.success(`Profile set to ${profileLabel(value)}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const block = (e) => required && e.preventDefault();

  return (
    <Dialog open={open} onOpenChange={(o) => !required && onOpenChange(o)}>
      <DialogContent
        data-testid="profile-type-modal"
        onEscapeKeyDown={block}
        onPointerDownOutside={block}
        onInteractOutside={block}
        className={`bg-[#14181D] border-white/10 text-white w-[calc(100%-2rem)] max-w-md rounded-2xl sm:rounded-2xl ${required ? "[&>button]:hidden" : ""}`}
      >
        <DialogHeader className="text-left sm:text-left">
          <span className="h-10 w-10 rounded-xl bg-[#00C896]/15 flex items-center justify-center text-[#00C896] mb-1">
            <UserCog className="h-5 w-5" />
          </span>
          <DialogTitle className="font-display text-xl">{required ? "Tell us a little about you" : "Profile type"}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {required
              ? "Pick the profile that fits you best so Finlo can tailor your tools. You can change this anytime from your account menu."
              : "Switch profiles to change which tools appear in Finlo. Your data is kept either way."}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <ProfileTypePicker value={value} onChange={setValue} testPrefix="modal-profile-type" />
        </div>
        <div>
        <Button
          data-testid="profile-type-save-button"
          onClick={save}
          disabled={!value || saving || value === user?.profile_type}
          className="mt-2 w-full h-11 rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : required ? "Continue" : "Save changes"}
        </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
